/**
 * AchievementEngine · evaluates triggers against global state.
 * Per `14-achievements-spec.md` §6.
 *
 * Pure-function design: takes a snapshot, returns newly earned badge IDs +
 * progress-bar updates. Caller (the hook · `app/src/hooks/useAchievements.ts`)
 * is responsible for committing.
 *
 * Moved from `app/src/lib/achievements/engine.ts` per ADR-008 · Phase 5
 * server-side re-evaluation imports from `@shared/engine/*` without rewrite.
 */

import type {
  AchievementsState,
  BadgeDefinition,
  BadgeTrigger,
  CapstoneState,
  Progress,
  RankType,
} from '../schemas';
import { BADGE_CATALOG } from './badge-catalog';

export interface EngineContext {
  progress: Progress;
  /**
   * Practice rank stats — sourced from `practiceStore.getRankStats()`.
   * Per-RankType counter incremented when scenario completes with matching
   * `rank` field prefix. CTFL counter is NOT scenario-driven — it lives in
   * `progress.cockpit.mockHistory` and is checked separately in matchRank.
   *
   * W3.1 extension 2026-05-04 · was `{ detective, author }` · now full
   * RankType coverage. Block 2 (api-master · sql-detective · security-watcher)
   * + Block 3 (automation-architect · ci-wrangler) + Block 4 (ai-qa-master)
   * counters supported. Backward-compatible: missing keys default to 0.
   */
  practiceStats: Partial<Record<RankType, number>>;
  /** Cumulative count of completed Bonus scenarios (Practice tier=bonus). */
  bonusScenariosCompleted: number;
  /** Glossary mastery count — derived from progress.cockpit.flashcards. */
  glossaryMasteredCount: number;
  /** Capstone snapshot for capstone-bonus-complete trigger (Block 1). */
  block1Capstone?: CapstoneState;
  /**
   * Block 2 capstone snapshot · per `release-unlock` v1.0 trigger.
   * W3.1 extension 2026-05-04 · used by matchTrigger 'release-unlock' when
   * version='v1.0'.
   */
  block2Capstone?: CapstoneState;
  /**
   * Block 3 capstone snapshot · per `release-unlock` v1.5 trigger.
   * W3.1 extension 2026-05-04 · used by matchTrigger 'release-unlock' when
   * version='v1.5'.
   */
  block3Capstone?: CapstoneState;
  /**
   * Per-sprint bonus engagement flag · per Genie ack 1 MAY 2026 (Option a)
   * for the redefined `atelier-resident-s04/s05/s06` counter:
   * `true` when ≥1 of {Bonus Practice task done · Mini-lesson read · Quiz pack solved}
   * is present for that sprint. Computed in `useAchievements` from the catalogs.
   */
  bonusSprintEngagement: Record<number, boolean>;
  /**
   * W4.1 extension 2026-05-04 · capability flags map для Tier 4 curiosity triggers.
   * Sourced от `progress.achievements.capabilityFlags` · keyed by flag name,
   * value = counter (0/1 для single-flip · N для cumulative threshold).
   * Optional · undefined treated as empty record.
   */
  capabilityFlags?: Record<string, number>;
}

export interface EvaluationResult {
  newlyEarned: BadgeDefinition[];
  progressUpdates: Record<string, number>;
}

const requiredTicketsForSprint = (sprintNum: number, ctx: EngineContext): boolean => {
  // Phase 1 simplification: a sprint is «Required complete» when its sprintProgress
  // reports `pctDone >= 100`. Once Required/Bonus tier metadata lands per ticket
  // (ATELIER_TASKS Phase 5 wiring), this will switch to «all Required tickets done».
  const sprintId = `S${sprintNum.toString().padStart(2, '0')}`;
  return (ctx.progress.sprints[sprintId]?.pctDone ?? 0) >= 100;
};

const allTicketsForSprint = (sprintNum: number, ctx: EngineContext): boolean => {
  // Per Genie ack 1 MAY 2026 (Option a · `14-achievements-spec.md` §2 row 8-10):
  // "All Required complete + ANY one bonus engagement keyed by sprint".
  // Bonus engagement is OR across {Bonus Practice task · Mini-lesson read · Quiz pack solved}
  // for that sprint, computed in `useAchievements` and surfaced via `bonusSprintEngagement`.
  if (!requiredTicketsForSprint(sprintNum, ctx)) return false;
  return ctx.bonusSprintEngagement[sprintNum] === true;
};

const matchRank = (rankType: RankType, threshold: number, ctx: EngineContext): boolean => {
  // CTFL counter is mock-exam-driven · not scenario rank field.
  if (rankType === 'ctfl') {
    return (ctx.progress.cockpit.mockHistory.mock2?.pct ?? 0) >= threshold;
  }
  // All other RankType values are scenario-driven · counted via practiceStats.
  return (ctx.practiceStats[rankType] ?? 0) >= threshold;
};

const rankProgress = (rankType: RankType, ctx: EngineContext): number | null => {
  if (rankType === 'ctfl') {
    return ctx.progress.cockpit.mockHistory.mock2?.pct ?? 0;
  }
  return ctx.practiceStats[rankType] ?? 0;
};

const matchSeniorExtensions = (
  trigger: Extract<BadgeTrigger, { kind: 'all-senior-extensions-opened' }>,
  ctx: EngineContext,
): boolean => {
  const opened = ctx.progress.achievements?.seniorExtensionsOpened ?? {};
  if (trigger.sprintNum != null) {
    const prefix = `S${trigger.sprintNum.toString().padStart(2, '0')}.`;
    const keys = Object.keys(opened).filter((k) => k.startsWith(prefix));
    return keys.length > 0 && keys.every((k) => opened[k] === true);
  }
  if (trigger.block === 'block-1') {
    // Earned only after all 6 per-sprint Owls
    const earned = ctx.progress.achievements?.earned ?? [];
    const owlIds = [1, 2, 3, 4, 5, 6].map(
      (n) => `owl-of-wisdom-sprint-${n.toString().padStart(2, '0')}`,
    );
    return owlIds.every((id) => earned.some((e) => e.badgeId === id));
  }
  return false;
};

const matchPolyglot = (ctx: EngineContext): boolean => {
  const a = ctx.progress.achievements;
  if (!a) return false;
  const themesUsed = ['warmsunset', 'coolmist', 'deepstudio'].every(
    (t) => (a.themeUsage[t] ?? 0) >= 1,
  );
  const localesUsed = ['ru', 'en'].every((l) => (a.localeUsage[l] ?? 0) >= 1);
  return themesUsed && localesUsed;
};

const matchPomodoroWindow = (
  trigger: Extract<BadgeTrigger, { kind: 'pomodoro-time-window' }>,
  ctx: EngineContext,
): boolean => {
  const sessions = ctx.progress.achievements?.pomodoroSessions ?? [];
  const filtered = sessions.filter((s) => {
    if (trigger.window === 'early-bird') return s.startHour < 8;
    if (trigger.window === 'night-owl') return s.startHour >= 23 || s.startHour < 5;
    return false;
  });
  return filtered.length >= trigger.threshold;
};

const matchFirstQuickCheck = (ctx: EngineContext): boolean => {
  return Object.values(ctx.progress.tickets).some((t) => t.quickCheck?.state === 'correct');
};

const matchCapstoneBonus = (
  trigger: Extract<BadgeTrigger, { kind: 'capstone-bonus-complete' }>,
  ctx: EngineContext,
): boolean => {
  if (trigger.capstoneId !== 'block-1') return false;
  const cap = ctx.block1Capstone;
  if (!cap) return false;
  const bonusValues = Object.values(cap.rubricBonus);
  return bonusValues.length > 0 && bonusValues.every((v) => v === true);
};

const matchTrigger = (trigger: BadgeTrigger, ctx: EngineContext): boolean => {
  switch (trigger.kind) {
    case 'sprint-required-complete':
      return requiredTicketsForSprint(trigger.sprintNum, ctx);
    case 'sprint-bonus-complete':
      return allTicketsForSprint(trigger.sprintNum, ctx);
    case 'release-unlock': {
      // W3.1 extension 2026-05-04 · per-block release-unlock check.
      // v0.5 = Block 1 Capstone · v1.0 = Block 2 Capstone · v1.5 = Block 3 Capstone.
      // Block 4 v2.0 reserved для Wave 4.
      const v = trigger.releaseVersion;
      if (v === 'v0.5') return Boolean(ctx.block1Capstone?.releaseUnlockedAt);
      if (v === 'v1.0') return Boolean(ctx.block2Capstone?.releaseUnlockedAt);
      if (v === 'v1.5') return Boolean(ctx.block3Capstone?.releaseUnlockedAt);
      return false;
    }
    case 'rank-threshold':
      return matchRank(trigger.rankType, trigger.threshold, ctx);
    case 'cumulative-bonus-count':
      return ctx.bonusScenariosCompleted >= trigger.threshold;
    case 'capstone-bonus-complete':
      return matchCapstoneBonus(trigger, ctx);
    case 'all-senior-extensions-opened':
      return matchSeniorExtensions(trigger, ctx);
    case 'theme-locale-polyglot':
      return matchPolyglot(ctx);
    case 'pomodoro-time-window':
      return matchPomodoroWindow(trigger, ctx);
    case 'first-quick-check-correct':
      return matchFirstQuickCheck(ctx);
    case 'all-glossary-mastered':
      return ctx.glossaryMasteredCount >= trigger.threshold;
    case 'capability-flag': {
      // W4.1 extension 2026-05-04 · capability flag matcher.
      // Cumulative threshold: counter ≥ threshold. Single-flip: counter ≥ 1 (default).
      const counter = ctx.capabilityFlags?.[trigger.flag] ?? 0;
      const threshold = trigger.threshold ?? 1;
      return counter >= threshold;
    }
  }
};

const computeProgress = (badge: BadgeDefinition, ctx: EngineContext): number | null => {
  const t = badge.trigger;
  switch (t.kind) {
    case 'rank-threshold': {
      const p = rankProgress(t.rankType, ctx);
      return p == null ? null : Math.min(p, t.threshold);
    }
    case 'cumulative-bonus-count':
      return Math.min(ctx.bonusScenariosCompleted, t.threshold);
    case 'pomodoro-time-window': {
      const sessions = ctx.progress.achievements?.pomodoroSessions ?? [];
      const filtered = sessions.filter((s) =>
        t.window === 'early-bird' ? s.startHour < 8 : s.startHour >= 23 || s.startHour < 5,
      );
      return Math.min(filtered.length, t.threshold);
    }
    case 'all-glossary-mastered':
      return Math.min(ctx.glossaryMasteredCount, t.threshold);
    case 'capability-flag': {
      // Show progress for cumulative threshold triggers (skip single-flip).
      if (!t.threshold || t.threshold === 1) return null;
      const counter = ctx.capabilityFlags?.[t.flag] ?? 0;
      return Math.min(counter, t.threshold);
    }
    default:
      return null;
  }
};

export function evaluate(
  achievements: AchievementsState,
  ctx: EngineContext,
): EvaluationResult {
  const newlyEarned: BadgeDefinition[] = [];
  const progressUpdates: Record<string, number> = {};
  const earnedSet = new Set(achievements.earned.map((e) => e.badgeId));

  for (const badge of BADGE_CATALOG) {
    if (earnedSet.has(badge.id)) continue;
    if (matchTrigger(badge.trigger, ctx)) {
      newlyEarned.push(badge);
    } else {
      const p = computeProgress(badge, ctx);
      if (p != null && achievements.progress[badge.id] !== p) {
        progressUpdates[badge.id] = p;
      }
    }
  }

  return { newlyEarned, progressUpdates };
}
